import { useCallback } from 'react';
import { noop } from 'lodash/fp';
import {
  Container,
  Content,
  Divider,
  Header,
  InPlaceInput,
  Inline,
  InlineItem,
  Menu,
  MenuItem,
  Text,
} from '@kinesis/bungle';
import { Layout } from '@/components/layout';
import { GHGProtocolIcon } from '@/components/ghg-protocol-icon';
import { PeriodMenuItem } from '@/components/period-menu-item';
import { PlaceMenuItem } from '@/components/place-menu-item';
import { ScenarioMenuItem } from '@/components/scenario-menu-item';
import { Disclosure, DisclosurePeriod, Place, Scenario } from '@/types';

// Editable disclosure header. Store-less: takes the current `disclosure`
// and an `onChange` callback that fires on every edit (name keystroke, scenario
// pick, period pick, place pick) — the page owns the `localDisclosure` +
// `hasChanges` state and decides when to call the update mutation. Mirrors
// suit's `components/disclosure-header/disclosure-header.tsx`.
//
// Menu rows, mirroring suit's split:
//   1. Disclosure-type label (GHG icon + "Community disclosure") carrying the
//      show/hide-configuration toggle on the right. Clicking the row flips
//      `showConfiguration` (owned by the page via a `?show=` URL param) and
//      collapses/expands the configuration rows below; the right-aligned
//      label flips between "Show configuration" and "Hide configuration".
//   2. Scenario-selector editor (S icon + the pinned scenario's name) — a
//      configuration row, shown only when the inventory has scenarios. The
//      scenario is a top-level field on the disclosure (NOT in `definition`).
//   3. Period editor (calendar icon + period text or "Configure period"
//      placeholder) — a configuration row.
//   4. Place-scope editor (map icon + the scoped place's name, or "Whole
//      inventory") — a configuration row, shown only when the inventory has
//      places to scope to. `places` is passed in by the page (components
//      don't fetch).
//
// The editors are the shared `*MenuItem` components — extracted when the
// pathway sidebar became their second consumer.

type DisclosureHeaderProps = {
  disclosure: Disclosure;
  onChange: (v: Disclosure) => void;
  scenarios?: Scenario[];
  places?: Place[];
  showConfiguration?: boolean;
  toggleConfiguration?: () => void;
};

const DisclosureHeader = (props: DisclosureHeaderProps) => {
  const {
    disclosure,
    onChange,
    scenarios = [],
    places = [],
    showConfiguration = true,
    toggleConfiguration = noop,
  } = props;

  const onChangeName = useCallback(
    (v: string) => {
      onChange({ ...disclosure, name: v });
    },
    [disclosure, onChange],
  );

  // The scenario is a top-level field on the disclosure (not the definition):
  // it's the ledger-build input the report renders against, not part of the
  // report's shape. Picking one re-runs the simulate queries against it live.
  const onChangeScenario = useCallback(
    (scenario: number) => {
      onChange({ ...disclosure, scenario });
    },
    [disclosure, onChange],
  );

  const onChangePeriod = useCallback(
    (period: DisclosurePeriod) => {
      onChange({
        ...disclosure,
        definition: { ...disclosure.definition, period },
      });
    },
    [disclosure, onChange],
  );

  // A place scope is just another field on the definition (like the period),
  // so picking one re-runs the simulate queries and re-scopes the report
  // live. `undefined` is the whole-inventory default; it drops off the wire
  // (JSON omits undefined), so village reads it back as no scope.
  const onChangePlace = useCallback(
    (place: number | undefined) => {
      onChange({
        ...disclosure,
        definition: { ...disclosure.definition, place },
      });
    },
    [disclosure, onChange],
  );

  // Same structure as suit's disclosure header: a bungle `Container >
  // Header` sitting in the disclosure grid's full-width section, with the
  // content offset left by the sidebar width (239px) so the title lines
  // up with the body beneath it.
  return (
    <Container>
      <Header border>
        <Content background='white' padding='none'>
          <div style={{ paddingLeft: '239px' }}>
            <Layout>
              <InPlaceInput
                value={disclosure.name}
                placeholder='Disclosure name'
                onChange={onChangeName}
                variant='title'
              />
              <Divider />
              <Menu>
                <MenuItem
                  icon={<GHGProtocolIcon />}
                  content={
                    <Inline>
                      <InlineItem sizing='fill-container'>
                        <Text>Community disclosure</Text>
                      </InlineItem>
                      <InlineItem sizing='fit-content'>
                        <Text tone='secondary'>
                          {showConfiguration
                            ? 'Hide configuration'
                            : 'Show configuration'}
                        </Text>
                      </InlineItem>
                    </Inline>
                  }
                  onClick={toggleConfiguration}
                />
                {showConfiguration && scenarios.length > 0 && (
                  <ScenarioMenuItem
                    scenarios={scenarios}
                    value={disclosure.scenario}
                    onChange={onChangeScenario}
                  />
                )}
                {showConfiguration && (
                  <PeriodMenuItem
                    period={disclosure.definition.period}
                    onChange={onChangePeriod}
                    description={
                      'Select the ending month for this disclosure. The ' +
                      'disclosure period is 12 months up to and including ' +
                      'the selected month.'
                    }
                  />
                )}
                {showConfiguration && places.length > 0 && (
                  <PlaceMenuItem
                    places={places}
                    value={disclosure.definition.place}
                    onChange={onChangePlace}
                  />
                )}
              </Menu>
            </Layout>
          </div>
        </Content>
      </Header>
    </Container>
  );
};

export type { DisclosureHeaderProps };
export { DisclosureHeader };
